import React from 'react';
import {
  Paper,
  Typography,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Divider,
  Box,
  Button,
  Slider,
  TextField,
  InputAdornment,
  Switch,
  Tooltip,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import StoreIcon from '@mui/icons-material/Store';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import LoyaltyOutlinedIcon from '@mui/icons-material/LoyaltyOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import LocalMallOutlinedIcon from '@mui/icons-material/LocalMallOutlined';
import RedeemOutlinedIcon from '@mui/icons-material/RedeemOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import FaceOutlinedIcon from '@mui/icons-material/FaceOutlined';
import PermIdentityOutlinedIcon from '@mui/icons-material/PermIdentityOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PedalBikeOutlinedIcon from '@mui/icons-material/PedalBikeOutlined';
import DeliveryDiningOutlinedIcon from '@mui/icons-material/DeliveryDiningOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import RestaurantMenuOutlinedIcon from '@mui/icons-material/RestaurantMenuOutlined';
import FlashOnOutlinedIcon from '@mui/icons-material/FlashOnOutlined';
import { useTranslation } from 'react-i18next';
import { getLogoImageStyle } from '../utils/logoImageStyle';
import { getHeaderTemplate, isHeaderOverlayActive } from '../utils/headerTemplates';

export type HeaderCommonSection = 'appearance' | 'logo' | 'visibility' | 'icons';

interface HeaderCommonSettingsProps {
  section: HeaderCommonSection;
  config: any;
  defaultConfig: any;
  currentLogoUrl: string | null;
  onHeaderSettingChange: (setting: any, value: any) => void;
  onOpenLogoLibrary: (type: 'desktop' | 'mobile') => void;
  onRemoveLogo?: () => void;
}

/**
 * Tüm header şablonlarının paylaştığı ortak ayarlar.
 * Sihirbaz adımlarına göre bölümlere ayrılmıştır:
 *  - appearance: blur / arama / boyut / renk
 *  - logo: logo yükleme + boyut
 *  - visibility: görünürlük / iletişim / şeritler (katlanabilir accordion gruplar)
 *  - icons: ikon seçimleri + (header6) sipariş butonu
 * Şablona özgü küçük bloklar capability ile gösterilir.
 */
const HeaderCommonSettings: React.FC<HeaderCommonSettingsProps> = ({
  section,
  config,
  defaultConfig,
  currentLogoUrl,
  onHeaderSettingChange,
  onOpenLogoLibrary,
  onRemoveLogo,
}) => {
  const { t } = useTranslation('salesSystem');

  const template = getHeaderTemplate(config.headerSettings?.headerType);
  const caps = template.capabilities;
  const isHeader5 = caps.builtInMegaMenu;
  const isHeader6 = caps.orderButton;
  const showMenuAlign = caps.builtInMegaMenu || caps.inlineNav;

  const effectiveLogoUrl = config.headerSettings?.logoUrl || currentLogoUrl;
  const isLogoVisible = config.headerSettings?.showLogo !== false;
  const searchType =
    config.headerSettings.searchType === 'none' || config.headerSettings.showSearch === false
      ? 'none'
      : (config.headerSettings.searchType || 'product');
  const blurOn = template.blurDefaultOn
    ? config.headerSettings.enableHeaderBlur !== false
    : config.headerSettings.enableHeaderBlur === true;
  const overlayOn = isHeaderOverlayActive(config.headerSettings);
  const backgroundColorValue = overlayOn ? '#ffffff' : (config.headerSettings.backgroundColor || '#ffffff');
  const compactCard = { p: 1.75, height: '100%' };

  if (section === 'appearance') {
    return (
      <Grid container spacing={1.5}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={compactCard}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                {t('ecommerceHeaderSettings.blur.title')}
              </Typography>
              <Tooltip title={isHeader5 ? t('ecommerceHeaderSettings.blur.help') : t('ecommerceHeaderSettings.blur.optionalHelp')}>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={blurOn}
                      onChange={(e) => onHeaderSettingChange('enableHeaderBlur', e.target.checked)}
                    />
                  }
                  label={t('ecommerceHeaderSettings.blur.enabled')}
                />
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Slider
                value={Number(config.headerSettings.headerBlurAmount ?? 12)}
                onChange={(_e, newValue) => onHeaderSettingChange('headerBlurAmount', newValue as number)}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value}px`}
                step={1}
                min={0}
                max={24}
                disabled={!blurOn}
                size="small"
              />
              <Typography variant="body2" sx={{ minWidth: 40 }}>
                {Number(config.headerSettings.headerBlurAmount ?? 12)}px
              </Typography>
              <Button size="small" disabled={!blurOn} onClick={() => onHeaderSettingChange('headerBlurAmount', 12)}>
                {t('ecommerceHeaderSettings.actions.reset')}
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={compactCard}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75 }}>
              {t('ecommerceHeaderSettings.searchSystemSettings')}
            </Typography>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={searchType}
              onChange={(_e, next) => {
                if (next) onHeaderSettingChange('searchType', next);
              }}
              sx={{ mb: 0.75 }}
            >
              <ToggleButton value="product" sx={{ textTransform: 'none', fontSize: 12, py: 0.4 }}>
                {t('ecommerceHeaderSettings.searchTypes.product.title')}
              </ToggleButton>
              <ToggleButton value="content" sx={{ textTransform: 'none', fontSize: 12, py: 0.4 }}>
                {t('ecommerceHeaderSettings.searchTypes.content.title')}
              </ToggleButton>
              <ToggleButton value="none" sx={{ textTransform: 'none', fontSize: 12, py: 0.4 }}>
                {t('ecommerceHeaderSettings.searchTypes.none.title')}
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary">
              {t(`ecommerceHeaderSettings.searchTypes.${searchType}.description`)}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={compactCard}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                {t('ecommerceHeaderSettings.sizingSettings')}
              </Typography>
              <Button
                size="small"
                onClick={() => {
                  onHeaderSettingChange('verticalPadding', defaultConfig.headerSettings.verticalPadding);
                  onHeaderSettingChange('header6MenuFontSize', defaultConfig.headerSettings.header6MenuFontSize || 14);
                }}
              >
                {t('ecommerceHeaderSettings.actions.reset')}
              </Button>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Slider
                size="small"
                value={config.headerSettings.verticalPadding || 16}
                onChange={(_e, newValue) => onHeaderSettingChange('verticalPadding', newValue as number)}
                valueLabelDisplay="auto"
                step={2}
                min={4}
                max={48}
              />
              <Typography variant="body2" sx={{ minWidth: 40 }}>
                {config.headerSettings.verticalPadding || 16}px
              </Typography>
            </Box>
            <Box sx={{ mt: 1.25 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                {t('ecommerceHeaderSettings.fontSize')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Slider
                  size="small"
                  value={config.headerSettings.header6MenuFontSize || 14}
                  onChange={(_e, newValue) => onHeaderSettingChange('header6MenuFontSize', newValue as number)}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value}px`}
                  step={1}
                  min={11}
                  max={24}
                />
                <Typography variant="body2" sx={{ minWidth: 40 }}>
                  {config.headerSettings.header6MenuFontSize || 14}px
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {t('ecommerceHeaderSettings.fontSizeHelp')}
              </Typography>
            </Box>
            {showMenuAlign && (
              <Box sx={{ mt: 1.25 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {t('ecommerceHeaderSettings.menuAlign.title')}
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  value={config.headerSettings.menuAlign || 'center'}
                  onChange={(_e, next) => {
                    if (next) onHeaderSettingChange('menuAlign', next);
                  }}
                >
                  <ToggleButton value="left" sx={{ textTransform: 'none', py: 0.4 }}>
                    {t('ecommerceHeaderSettings.menuAlign.left')}
                  </ToggleButton>
                  <ToggleButton value="center" sx={{ textTransform: 'none', py: 0.4 }}>
                    {t('ecommerceHeaderSettings.menuAlign.center')}
                  </ToggleButton>
                  <ToggleButton value="right" sx={{ textTransform: 'none', py: 0.4 }}>
                    {t('ecommerceHeaderSettings.menuAlign.right')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={compactCard}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" fontWeight={700}>
                {t('ecommerceHeaderSettings.colorSettings')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 12 }}
                  onClick={() => {
                    onHeaderSettingChange('backgroundColor', '#ffffff');
                    onHeaderSettingChange('textColor', '#212121');
                  }}
                >
                  {t('ecommerceHeaderSettings.colorPresetLight')}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 12 }}
                  onClick={() => {
                    onHeaderSettingChange('backgroundColor', '#000000');
                    onHeaderSettingChange('textColor', '#ffffff');
                  }}
                >
                  {t('ecommerceHeaderSettings.colorPresetDark')}
                </Button>
              </Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <TextField
                fullWidth
                label={t('ecommerceHeaderSettings.backgroundColor')}
                value={overlayOn ? t('ecommerceHeaderSettings.header7.overlayTransparent') : config.headerSettings.backgroundColor}
                onChange={(e) => onHeaderSettingChange('backgroundColor', e.target.value)}
                size="small"
                disabled={overlayOn}
                helperText={overlayOn ? t('ecommerceHeaderSettings.header7.overlayLocksBackground') : undefined}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        component="input"
                        type="color"
                        value={backgroundColorValue}
                        onChange={(e: any) => onHeaderSettingChange('backgroundColor', e.target.value)}
                        sx={{ border: 'none', width: 22, height: 22, p: 0, background: 'none', cursor: 'pointer' }}
                        disabled={overlayOn}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label={t('ecommerceHeaderSettings.textAndIconColor')}
                value={config.headerSettings.textColor}
                onChange={(e) => onHeaderSettingChange('textColor', e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        component="input"
                        type="color"
                        value={config.headerSettings.textColor}
                        onChange={(e: any) => onHeaderSettingChange('textColor', e.target.value)}
                        sx={{ border: 'none', width: 22, height: 22, p: 0, background: 'none', cursor: 'pointer' }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );
  }

  if (section === 'logo') {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('ecommerceHeaderSettings.logo.currentLogo')}
            </Typography>
            {effectiveLogoUrl ? (
              <Box
                component="img"
                src={effectiveLogoUrl}
                alt={t('ecommerceHeaderSettings.logo.companyLogoAlt')}
                sx={{
                  ...getLogoImageStyle({
                    width: config.headerSettings.logoWidth || 120,
                    height: config.headerSettings.logoHeight || 50,
                  }),
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 0.5,
                  mx: 'auto',
                }}
              />
            ) : (
              <Box sx={{
                width: 80,
                height: 80,
                bgcolor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
                border: '1px solid #e0e0e0',
                mx: 'auto'
              }}>
                <StoreIcon sx={{ color: 'grey.500' }} />
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={8}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>{t('ecommerceHeaderSettings.logo.selectLogo')}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  onClick={() => onOpenLogoLibrary('desktop')}
                  startIcon={<PhotoLibraryIcon />}
                >
                  {t('ecommerceHeaderSettings.actions.selectFromImageLibrary')}
                </Button>
                {effectiveLogoUrl && onRemoveLogo && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={onRemoveLogo}
                    startIcon={<RestartAltIcon />}
                  >
                    {t('ecommerceHeaderSettings.actions.removeLogo')}
                  </Button>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                💡 {t('ecommerceHeaderSettings.logo.selectLogoHelp')}
              </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('ecommerceHeaderSettings.logo.sizeSettingWidth')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1 }}>
                <Slider
                  value={config.headerSettings.logoWidth || 120}
                  onChange={(e, newValue) => onHeaderSettingChange('logoWidth', newValue)}
                  aria-labelledby="logo-size-slider"
                  valueLabelDisplay="auto"
                  step={10}
                  min={50}
                  max={500}
                  disabled={!isLogoVisible}
                />
                <Typography variant="body1" sx={{ minWidth: '40px' }}>
                  {config.headerSettings.logoWidth || 120}px
                </Typography>
              </Box>
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('ecommerceHeaderSettings.logo.sizeSettingHeight')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1 }}>
                <Slider
                  value={config.headerSettings.logoHeight || 50}
                  onChange={(e, newValue) => onHeaderSettingChange('logoHeight', newValue)}
                  aria-labelledby="logo-height-slider"
                  valueLabelDisplay="auto"
                  step={5}
                  min={20}
                  max={200}
                  disabled={!isLogoVisible}
                />
                <Typography variant="body1" sx={{ minWidth: '40px' }}>
                  {config.headerSettings.logoHeight || 50}px
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, px: 1 }}>
                {t('ecommerceHeaderSettings.logo.sizeSettingHeightHelp')}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    );
  }

  if (section === 'visibility') {
    return (
      <>
        <Accordion defaultExpanded disableGutters sx={{ '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography id="header-section-visibility" variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              {t('ecommerceHeaderSettings.visibilitySettings')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isLogoVisible}
                      onChange={(e) => onHeaderSettingChange('showLogo', e.target.checked)}
                    />
                  }
                  label={t('ecommerceHeaderSettings.toggles.showLogo')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.headerSettings.showSearch}
                      onChange={(e) => onHeaderSettingChange('showSearch', e.target.checked)}
                    />
                  }
                  label={t('ecommerceHeaderSettings.toggles.showSearchBar')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.headerSettings.showCart}
                      onChange={(e) => onHeaderSettingChange('showCart', e.target.checked)}
                    />
                  }
                  label={t('ecommerceHeaderSettings.toggles.showCartIcon')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.headerSettings.showUserMenu}
                      onChange={(e) => onHeaderSettingChange('showUserMenu', e.target.checked)}
                    />
                  }
                  label={t('ecommerceHeaderSettings.toggles.showFavoriteIcon')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.headerSettings.showUserIcon}
                      onChange={(e) => onHeaderSettingChange('showUserIcon', e.target.checked)}
                    />
                  }
                  label={t('ecommerceHeaderSettings.toggles.showUserIcon')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.headerSettings.showMobileCategories}
                      onChange={(e) => onHeaderSettingChange('showMobileCategories', e.target.checked)}
                    />
                  }
                  label={t('ecommerceHeaderSettings.toggles.showCategoriesButtonMobile')}
                />
              </Grid>
              {caps.phoneBranches && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.headerSettings.showPhoneButton === true}
                          onChange={(e) => onHeaderSettingChange('showPhoneButton', e.target.checked)}
                        />
                      }
                      label={t('ecommerceHeaderSettings.contactButtons.showPhone')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.headerSettings.showBranchesButton === true}
                          onChange={(e) => onHeaderSettingChange('showBranchesButton', e.target.checked)}
                        />
                      }
                      label={t('ecommerceHeaderSettings.contactButtons.showBranches')}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.headerSettings.showBecomeSellerButton || false}
                      onChange={(e) => onHeaderSettingChange('showBecomeSellerButton', e.target.checked)}
                    />
                  }
                  label={t('ecommerceHeaderSettings.toggles.showBecomeSellerButton')}
                />
              </Grid>
              {caps.bottomNavbar && (
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.headerSettings.showBottomNavbar !== false}
                        onChange={(e) => onHeaderSettingChange('showBottomNavbar', e.target.checked)}
                      />
                    }
                    label={t('ecommerceHeaderSettings.toggles.showBottomNavbar')}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -0.5 }}>
                    {t('ecommerceHeaderSettings.toggles.showBottomNavbarHelp')}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {caps.phoneBranches && (
          <Accordion disableGutters sx={{ '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {t('ecommerceHeaderSettings.contactButtons.title')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                {t('ecommerceHeaderSettings.contactButtons.help')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('ecommerceHeaderSettings.contactButtons.phoneText')}
                    value={config.headerSettings.phoneText || '444 61 62'}
                    onChange={(e) => onHeaderSettingChange('phoneText', e.target.value)}
                    disabled={config.headerSettings.showPhoneButton !== true}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('ecommerceHeaderSettings.contactButtons.phoneUrl')}
                    value={config.headerSettings.phoneUrl || 'tel:4446162'}
                    onChange={(e) => onHeaderSettingChange('phoneUrl', e.target.value)}
                    disabled={config.headerSettings.showPhoneButton !== true}
                    helperText={t('ecommerceHeaderSettings.contactButtons.phoneUrlHelp')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('ecommerceHeaderSettings.contactButtons.branchesText')}
                    value={config.headerSettings.branchesText || 'Şubeler'}
                    onChange={(e) => onHeaderSettingChange('branchesText', e.target.value)}
                    disabled={config.headerSettings.showBranchesButton !== true}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('ecommerceHeaderSettings.contactButtons.branchesUrl')}
                    value={config.headerSettings.branchesUrl || '/branches'}
                    onChange={(e) => onHeaderSettingChange('branchesUrl', e.target.value)}
                    disabled={config.headerSettings.showBranchesButton !== true}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('ecommerceHeaderSettings.contactButtons.pillBackground')}
                    value={config.headerSettings.contactButtonBackgroundColor || '#2a2a2a'}
                    onChange={(e) => onHeaderSettingChange('contactButtonBackgroundColor', e.target.value)}
                    disabled={config.headerSettings.showPhoneButton !== true && config.headerSettings.showBranchesButton !== true}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            component="input"
                            type="color"
                            value={config.headerSettings.contactButtonBackgroundColor || '#2a2a2a'}
                            onChange={(e: any) => onHeaderSettingChange('contactButtonBackgroundColor', e.target.value)}
                            sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                            disabled={config.headerSettings.showPhoneButton !== true && config.headerSettings.showBranchesButton !== true}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('ecommerceHeaderSettings.contactButtons.pillText')}
                    value={config.headerSettings.contactButtonTextColor || '#ffffff'}
                    onChange={(e) => onHeaderSettingChange('contactButtonTextColor', e.target.value)}
                    disabled={config.headerSettings.showPhoneButton !== true && config.headerSettings.showBranchesButton !== true}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            component="input"
                            type="color"
                            value={config.headerSettings.contactButtonTextColor || '#ffffff'}
                            onChange={(e: any) => onHeaderSettingChange('contactButtonTextColor', e.target.value)}
                            sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                            disabled={config.headerSettings.showPhoneButton !== true && config.headerSettings.showBranchesButton !== true}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}

        {caps.promoStrips && (
          <Accordion disableGutters sx={{ '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Header Bilgi/Kampanya Şeritleri
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.headerSettings.showHeaderInfoStrip === true}
                        onChange={(e) => onHeaderSettingChange('showHeaderInfoStrip', e.target.checked)}
                      />
                    }
                    label="Üst Bilgi Şeridini Göster"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Üst Bilgi Şeridi Metni"
                    value={config.headerSettings.headerInfoStripText ?? ''}
                    onChange={(e) => onHeaderSettingChange('headerInfoStripText', e.target.value)}
                    disabled={config.headerSettings.showHeaderInfoStrip !== true}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Üst Şerit Arka Plan"
                    value={config.headerSettings.headerInfoStripBackgroundColor || '#7ED957'}
                    onChange={(e) => onHeaderSettingChange('headerInfoStripBackgroundColor', e.target.value)}
                    disabled={config.headerSettings.showHeaderInfoStrip !== true}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            component="input"
                            type="color"
                            value={config.headerSettings.headerInfoStripBackgroundColor || '#7ED957'}
                            onChange={(e: any) => onHeaderSettingChange('headerInfoStripBackgroundColor', e.target.value)}
                            sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                            disabled={config.headerSettings.showHeaderInfoStrip !== true}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Üst Şerit Yazı Rengi"
                    value={config.headerSettings.headerInfoStripTextColor || '#111111'}
                    onChange={(e) => onHeaderSettingChange('headerInfoStripTextColor', e.target.value)}
                    disabled={config.headerSettings.showHeaderInfoStrip !== true}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            component="input"
                            type="color"
                            value={config.headerSettings.headerInfoStripTextColor || '#111111'}
                            onChange={(e: any) => onHeaderSettingChange('headerInfoStripTextColor', e.target.value)}
                            sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                            disabled={config.headerSettings.showHeaderInfoStrip !== true}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>

                <Grid item xs={12} sx={{ pt: '8px !important' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.headerSettings.showHeaderMarqueeStrip === true}
                        onChange={(e) => onHeaderSettingChange('showHeaderMarqueeStrip', e.target.checked)}
                      />
                    }
                    label="Animasyonlu Kampanya Şeridini Göster"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Kampanya Şeridi Metni"
                    value={config.headerSettings.headerMarqueeStripText ?? ''}
                    onChange={(e) => onHeaderSettingChange('headerMarqueeStripText', e.target.value)}
                    disabled={config.headerSettings.showHeaderMarqueeStrip !== true}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Kampanya Arka Plan"
                    value={config.headerSettings.headerMarqueeStripBackgroundColor || '#0f172a'}
                    onChange={(e) => onHeaderSettingChange('headerMarqueeStripBackgroundColor', e.target.value)}
                    disabled={config.headerSettings.showHeaderMarqueeStrip !== true}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            component="input"
                            type="color"
                            value={config.headerSettings.headerMarqueeStripBackgroundColor || '#0f172a'}
                            onChange={(e: any) => onHeaderSettingChange('headerMarqueeStripBackgroundColor', e.target.value)}
                            sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                            disabled={config.headerSettings.showHeaderMarqueeStrip !== true}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Kampanya Yazı Rengi"
                    value={config.headerSettings.headerMarqueeStripTextColor || '#ffffff'}
                    onChange={(e) => onHeaderSettingChange('headerMarqueeStripTextColor', e.target.value)}
                    disabled={config.headerSettings.showHeaderMarqueeStrip !== true}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            component="input"
                            type="color"
                            value={config.headerSettings.headerMarqueeStripTextColor || '#ffffff'}
                            onChange={(e: any) => onHeaderSettingChange('headerMarqueeStripTextColor', e.target.value)}
                            sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                            disabled={config.headerSettings.showHeaderMarqueeStrip !== true}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Kampanya Animasyon Hızı (saniye)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1 }}>
                    <Slider
                      value={Number(config.headerSettings.headerMarqueeSpeedSeconds || 18)}
                      onChange={(e, newValue) => onHeaderSettingChange('headerMarqueeSpeedSeconds', Number(newValue))}
                      aria-labelledby="header-marquee-speed-slider"
                      valueLabelDisplay="auto"
                      step={1}
                      min={6}
                      max={60}
                      disabled={config.headerSettings.showHeaderMarqueeStrip !== true}
                    />
                    <Typography variant="body1" sx={{ minWidth: '40px' }}>
                      {Number(config.headerSettings.headerMarqueeSpeedSeconds || 18)}s
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}
      </>
    );
  }

  // section === 'icons'
  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Grid container spacing={4}>
        {/* Favori İkon Seçimi */}
        <Grid item xs={12} md={3}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>{t('ecommerceHeaderSettings.iconGroups.favoriteIcon')}</FormLabel>
            <RadioGroup
              name="favoriteIcon"
              value={config.headerSettings.favoriteIcon || 'favorite-1'}
              onChange={(e) => onHeaderSettingChange('favoriteIcon', e.target.value)}
            >
              <FormControlLabel value="favorite-1" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><FavoriteBorderIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 1 })}</Box>} />
              <FormControlLabel value="favorite-2" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><BookmarkBorderIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 2 })}</Box>} />
              <FormControlLabel value="favorite-3" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><StarBorderIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 3 })}</Box>} />
              <FormControlLabel value="favorite-4" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><ThumbUpOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 4 })}</Box>} />
              <FormControlLabel value="favorite-5" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><LoyaltyOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 5 })}</Box>} />
            </RadioGroup>
          </FormControl>
        </Grid>

        {/* Sepet İkon Seçimi */}
        <Grid item xs={12} md={3}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>{t('ecommerceHeaderSettings.iconGroups.cartIcon')}</FormLabel>
            <RadioGroup
              name="cartIcon"
              value={config.headerSettings.cartIcon || 'cart-1'}
              onChange={(e) => onHeaderSettingChange('cartIcon', e.target.value)}
            >
              <FormControlLabel value="cart-1" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><ShoppingBagOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 1 })}</Box>} />
              <FormControlLabel value="cart-2" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><ShoppingCartOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 2 })}</Box>} />
              <FormControlLabel value="cart-3" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><LocalMallOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 3 })}</Box>} />
              <FormControlLabel value="cart-4" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><RedeemOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 4 })}</Box>} />
              <FormControlLabel value="cart-5" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><StorefrontIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 5 })}</Box>} />
            </RadioGroup>
          </FormControl>
        </Grid>

        {/* Kullanıcı İkon Seçimi */}
        <Grid item xs={12} md={3}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>{t('ecommerceHeaderSettings.iconGroups.userIcon')}</FormLabel>
            <RadioGroup
              name="userIcon"
              value={config.headerSettings.userIcon || 'user-1'}
              onChange={(e) => onHeaderSettingChange('userIcon', e.target.value)}
            >
              <FormControlLabel value="user-1" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><AccountCircleOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 1 })}</Box>} />
              <FormControlLabel value="user-2" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><PersonOutlineIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 2 })}</Box>} />
              <FormControlLabel value="user-3" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><FaceOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 3 })}</Box>} />
              <FormControlLabel value="user-4" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><PermIdentityOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 4 })}</Box>} />
              <FormControlLabel value="user-5" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><VerifiedUserOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 5 })}</Box>} />
            </RadioGroup>
          </FormControl>
        </Grid>

        {isHeader6 && (
          <Grid item xs={12} md={3}>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>Sipariş Butonu İkonu</FormLabel>
              <RadioGroup
                name="orderButtonIcon"
                value={config.headerSettings.orderButtonIcon || 'order-1'}
                onChange={(e) => onHeaderSettingChange('orderButtonIcon', e.target.value)}
                sx={{ opacity: config.headerSettings.showOrderButton === true ? 1 : 0.55 }}
              >
                <FormControlLabel value="order-1" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><PedalBikeOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 1 })}</Box>} />
                <FormControlLabel value="order-2" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><DeliveryDiningOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 2 })}</Box>} />
                <FormControlLabel value="order-3" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><LocalShippingOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 3 })}</Box>} />
                <FormControlLabel value="order-4" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><RestaurantMenuOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 4 })}</Box>} />
                <FormControlLabel value="order-5" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><FlashOnOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 5 })}</Box>} />
              </RadioGroup>
            </FormControl>
          </Grid>
        )}
      </Grid>
      <Divider sx={{ my: 3 }} />
      <Box>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
          {t('ecommerceHeaderSettings.iconSizeSetting')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1 }}>
          <Slider
            value={config.headerSettings.iconSize || 24}
            onChange={(e, newValue) => onHeaderSettingChange('iconSize', newValue)}
            aria-labelledby="icon-size-slider"
            valueLabelDisplay="auto"
            step={1}
            min={16}
            max={48}
          />
          <Typography variant="body1" sx={{ minWidth: '40px' }}>
            {config.headerSettings.iconSize || 24}px
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, px: 1 }}>
          {t('ecommerceHeaderSettings.iconSizeSettingHelp')}
        </Typography>
      </Box>
      {isHeader6 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
            {t('ecommerceHeaderSettings.actionButtons.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {t('ecommerceHeaderSettings.actionButtons.help')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                size="small"
                label={t('ecommerceHeaderSettings.actionButtons.style')}
                value={config.headerSettings.actionIconStyle || 'plain'}
                onChange={(e) => onHeaderSettingChange('actionIconStyle', e.target.value)}
              >
                <MenuItem value="plain">{t('ecommerceHeaderSettings.actionButtons.stylePlain')}</MenuItem>
                <MenuItem value="circle">{t('ecommerceHeaderSettings.actionButtons.styleCircle')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label={t('ecommerceHeaderSettings.actionButtons.background')}
                value={config.headerSettings.actionIconBackgroundColor || '#f97316'}
                onChange={(e) => onHeaderSettingChange('actionIconBackgroundColor', e.target.value)}
                disabled={config.headerSettings.actionIconStyle !== 'circle'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        component="input"
                        type="color"
                        value={config.headerSettings.actionIconBackgroundColor || '#f97316'}
                        onChange={(e: any) => onHeaderSettingChange('actionIconBackgroundColor', e.target.value)}
                        sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                        disabled={config.headerSettings.actionIconStyle !== 'circle'}
                      />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label={t('ecommerceHeaderSettings.actionButtons.iconColor')}
                value={config.headerSettings.actionIconColor || '#ffffff'}
                onChange={(e) => onHeaderSettingChange('actionIconColor', e.target.value)}
                disabled={config.headerSettings.actionIconStyle !== 'circle'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        component="input"
                        type="color"
                        value={config.headerSettings.actionIconColor || '#ffffff'}
                        onChange={(e: any) => onHeaderSettingChange('actionIconColor', e.target.value)}
                        sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                        disabled={config.headerSettings.actionIconStyle !== 'circle'}
                      />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Header6 Sipariş Butonu Görünümü
          </Typography>
          <FormControlLabel
            sx={{ mb: 1 }}
            control={
              <Switch
                checked={config.headerSettings.showOrderButton === true}
                onChange={(e) => onHeaderSettingChange('showOrderButton', e.target.checked)}
              />
            }
            label="Sipariş Butonunu Göster"
          />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Buton Metni"
                value={config.headerSettings.orderButtonText || 'SİPARİŞ VER'}
                onChange={(e) => onHeaderSettingChange('orderButtonText', e.target.value)}
                size="small"
                disabled={config.headerSettings.showOrderButton !== true}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Buton Linki"
                value={config.headerSettings.orderButtonUrl || '/cart'}
                onChange={(e) => onHeaderSettingChange('orderButtonUrl', e.target.value)}
                size="small"
                placeholder="/cart"
                disabled={config.headerSettings.showOrderButton !== true}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Buton Arka Plan Rengi"
                value={config.headerSettings.orderButtonBackgroundColor || '#e63946'}
                onChange={(e) => onHeaderSettingChange('orderButtonBackgroundColor', e.target.value)}
                size="small"
                disabled={config.headerSettings.showOrderButton !== true}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        component="input"
                        type="color"
                        value={config.headerSettings.orderButtonBackgroundColor || '#e63946'}
                        onChange={(e: any) => onHeaderSettingChange('orderButtonBackgroundColor', e.target.value)}
                        sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                        disabled={config.headerSettings.showOrderButton !== true}
                      />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Buton Yazı/İkon Rengi"
                value={config.headerSettings.orderButtonTextColor || '#ffffff'}
                onChange={(e) => onHeaderSettingChange('orderButtonTextColor', e.target.value)}
                size="small"
                disabled={config.headerSettings.showOrderButton !== true}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        component="input"
                        type="color"
                        value={config.headerSettings.orderButtonTextColor || '#ffffff'}
                        onChange={(e: any) => onHeaderSettingChange('orderButtonTextColor', e.target.value)}
                        sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                        disabled={config.headerSettings.showOrderButton !== true}
                      />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>
        </>
      )}
    </Paper>
  );
};

export default HeaderCommonSettings;
